import {
    DriverAPI,
    ITransaction,
    LotI,
    NftAPI,
    NFTI,
    ScSet,
    SuccessTransactionResponseCb,
    WalletProviderE,
} from '@tacans/shared-marketplace-layer';
import { useCallback, useRef, useState } from 'react';
import { useGetNftWalletProvider } from 'src/features/common/collectible/hooks/use-get-nft-wallet-provider';
import { useAllCurrencyEntities } from 'src/features/common/currency/hooks/currency.hooks';
import { useGetDefaultWallet } from 'src/features/common/wallet/hooks/wallet.hooks';
import { ConcordiumQrModalAdapter } from 'src/services/concordium/concordium-qr-modal';
import { set } from 'src/services/concordium/concordium.registry';
import { useNotifications } from 'src/services/notifications/use-notifications';
import transactionsRegistry from 'src/services/transactions/transactions-registry';
import { ITransactionStatusE } from 'src/services/transactions/types';

export const useTokenTransaction = () => {
    const [initialization, setInitialization] = useState(false);
    const allCurrencyEntities = useAllCurrencyEntities();
    const { success, error: errorNotification } = useNotifications();
    const getNftWalletProvider = useGetNftWalletProvider();
    const getDefaultWallet = useGetDefaultWallet();
    const transaction = useRef<ITransaction>();

    const handleSuccessTransaction = useCallback(
        async (payload: SuccessTransactionResponseCb) => {
            const { transactionHash, method, nft, lot, walletProvider, trMethodName } = payload;

            transactionsRegistry.add({
                status: ITransactionStatusE.Pending,
                txHash: transactionHash,
                trMethodName,
                token: nft,
                lot,
                contractMethod: method,
                walletProvider,
            });

            success({
                text: 'Transaction has successfully sent',
            });
        },
        [success]
    );

    const createInstance = useCallback(
        async (nft: NFTI, lot: LotI) => {
            const walletProvider = getNftWalletProvider(nft);
            const chainWallet = getDefaultWallet(walletProvider);

            if (!chainWallet) {
                return;
            }

            const addressResponse = await NftAPI.fetchScAddress(nft.id);

            const { address = '' } = addressResponse.data;
            const [error] = addressResponse.data?.responseMeta?.error?.messages || [];

            if (error) {
                errorNotification({ text: error });
                throw new Error(error);
            }

            let scSet = {} as ScSet;

            const scInfoPayload = address ? { address } : { standard: nft.standard.toUpperCase() };
            if (walletProvider === WalletProviderE.Ethereum) {
                const res = await DriverAPI.getEthereumScInfo(scInfoPayload);
                scSet = res.data;
            } else {
                const res = await DriverAPI.getConcordiumScInfo(scInfoPayload);
                scSet = res.data;
            }
            // @ts-ignore
            // eslint-disable-next-line import/no-unresolved
            const { TransactionClientAdapter } = await import('@tacans/shared-marketplace-layer/transaction');

            transaction.current = new TransactionClientAdapter({
                nft,
                lot,
                walletProvider,
                scSet,
                connectedWalletAddress: chainWallet.address,
                concordiumModalAdapter: ConcordiumQrModalAdapter,
            });
        },
        [getDefaultWallet, getNftWalletProvider]
    );

    const createTransaction = useCallback(
        async (nft: NFTI, nftLot: LotI): Promise<ITransaction | undefined> => {
            setInitialization(true);
            const lot = { ...nftLot };
            try {
                await createInstance(nft, lot);
            } catch (err) {
                console.error('createInstance', err);
                setInitialization(false);
                return;
            }
            await setInitialization(false);
            // TODO - localization
            transaction.current
                ?.onPrepareTransaction(({ lot, transactionPayload }) => {
                    set('transaction', transactionPayload);
                    set('exchangeRate', allCurrencyEntities[lot.coinId]?.euro);
                })
                ?.onSuccess(handleSuccessTransaction)
                ?.onFailure(() => {
                    errorNotification({
                        text: 'Something went wrong. Please check transaction details in your wallet.',
                        timeout: 5000,
                    });
                })
                ?.onReject(() => {
                    errorNotification({
                        text: 'Request was rejected',
                    });
                });
            // eslint-disable-next-line consistent-return
            return transaction.current!;
        },
        [allCurrencyEntities, handleSuccessTransaction, createInstance, errorNotification]
    );

    return {
        initialization,
        createTransaction,
    };
};